import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import {RINGBA_DNI_PHONE, formatPhoneNumber} from "@/lib/constants";
export const meta = {
  title: "What is Flood Insurance?",
  description: "Although commonly thought of as supplementary, flood insurance coverage continues to gain popularity as homeowners seek protection from damaging floodwaters.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_100734575-scaled.webp",
    alt: "What is Flood Insurance?"
  },
  author: {
    name: "Rebecca Henderson, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "July 3, 2025",
  updatedAt: "July 3, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    hr: "hr",
    img: "img",
    li: "li",
    p: "p",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["Although nearly every U.S. county (99%) has experienced flooding since the 1990s, only 4% of Americans have flood insurance, according to the ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/get-flood-insurance",
        children: "Federal Emergency Management Agency"
      }), " (FEMA). Damage resulting from a flood, which is not covered by standard ", _jsx(_components.a, {
        href: "/insurance/home",
        children: "home insurance"
      }), ", can leave homeowners vulnerable to significant recuperative costs, many of which must be paid out of pocket or savings."]
    }), "\n", _jsx(_components.p, {
      children: "By examining and comparing supplementary coverage flood insurance provides in addition to a standard homeowner’s policy, homeowners can make a more informed decision regarding a flood insurance policy’s role in protecting their homes. Recognizing the benefits of more comprehensive coverage with flood insurance onboard can also help homeowners achieve greater peace of mind should a flood occur. Interested homeowners can also turn to multiple resources for more information on flood insurance, including FEMA and the Insurance Institute."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Two in five (41%) homeowners we surveyed incorrectly believed a standard homeowners insurance policy covers flood damage, leaving them vulnerable to costly gaps in coverage after a flood."
      }), "\n", _jsx(_components.li, {
        children: "Homeowners must wait 30 days before flood insurance coverage begins, so advanced planning is essential for protection against imminent storms."
      }), "\n", _jsx(_components.li, {
        children: "The national median cost for flood insurance is $786 annually, ranging from $412 in Alaska to $1,700 in Connecticut."
      }), "\n", _jsx(_components.li, {
        children: "More than 40% of all flood claims originate in low- to moderate-risk areas, with inaccurate floodplain mapping and changing climate conditions putting homes outside designated flood zones at significant risk."
      }), "\n", _jsx(_components.li, {
        children: "Just one inch of floodwater can cause $25,000 of damage to a home."
      }), "\n", _jsx(_components.li, {
        children: "If the National Flood Insurance Program (NFIP) fails to receive reauthorization after September 30, 2025, existing NFIP policyholders will remain covered until their policies expire, but no new NFIP policies will be issued, potentially affecting millions of homeowners who rely on this federal program for flood coverage."
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "What is Flood Insurance and What Does It Cover?"
    }), "\n", _jsxs(_components.p, {
      children: ["A flood insurance policy protects your home in the event that water enters and damages your home’s structure and contents, such as during ", _jsx(_components.a, {
        href: "/home-insurance/hurricane-season-insurance-guide",
        children: "hurricane season"
      }), ". Flood insurance policies are underwritten by the National Flood Insurance Program (NFIP), but are serviced through private insurance carriers, according to ", _jsx(_components.a, {
        href: "https://www.fema.gov/flood-insurance",
        children: "FEMA"
      }), ". Homeowners can also purchase flood insurance through an independent insurance carrier that may offer higher levels or additional coverage, according to the ", _jsx(_components.a, {
        href: "https://www.iii.org/article/do-i-need-flood-insurance-for-my-home",
        children: "Insurance Information Institute"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["Two in five homeowners (41%) incorrectly believe that a standard homeowners insurance policy covers flood damage, according to our 2025 homeowner survey. “Flood insurance is critical to have and covers water that rises from the surface or otherwise pools and causes a loss. Standard policies don’t cover any of that,” says Ted Patestos, CEO and Founder of ", _jsx(_components.a, {
        href: "https://www.tigeradjusters.com/",
        children: "Tiger Adjusters"
      }), ". Use the chart below to discover the difference in coverage between flood insurance and standard homeowners insurance policies:"]
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Flood Insurance"
          }), _jsx(_components.th, {
            children: "Standard Policy"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Covers water damage from surface flooding due to storms, hurricanes, and overflowing rivers/lakes"
          }), _jsx(_components.td, {
            children: "Does NOT cover flood damage or water rising from the surface"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Does NOT cover internal plumbing or wind-driven rain"
          }), _jsx(_components.td, {
            children: "Covers wind-driven rain, burst pipes, and appliance leaks"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "30-day waiting period before coverage begins"
          }), _jsx(_components.td, {
            children: "Immediate coverage when the policy activates"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "NFIP-administered with different compliance rules"
          }), _jsx(_components.td, {
            children: "Private insurance company claims handling"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Does NOT cover additional living expenses"
          }), _jsx(_components.td, {
            children: "Includes temporary living expenses"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "May exclude tile floors without an engineer’s report"
          }), _jsx(_components.td, {
            children: "Standard coverage for flooring/contents"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.p, {
      children: ["Most flood insurance policies require a 30-day waiting period before coverage begins, unless you’re actively purchasing a home in a high-risk flood area with a government-backed mortgage, per ", _jsx(_components.a, {
        href: "https://www.fema.gov/flood-insurance",
        children: "FEMA"
      }), ". Coverage under a flood insurance policy extends to your home’s structure (building) and your personal belongings (contents), but any claims payouts only extend to the policy limit. Building coverage limits include up to $250,000 in replacement costs, while contents coverage limits extend to $100,000 in actual cash value, or replacement costs less depreciation, according to ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/get-insured/buy-a-policy",
        children: "FEMA"
      }), "."]
    }), "\n", _jsx("iframe", {
      loading: "lazy",
      src: "https://e.infogram.com/d4302a71-ced1-4606-8a0b-5673c5b49bc3?src=embed&embed_type=responsive_iframe",
      title: "flood insurance quote 1",
      allowFullScreen: "",
      allow: "fullscreen"
    }), "\n", _jsxs(_components.p, {
      children: ["Homeowners must purchase building and contents coverage separately, each with its corresponding deductible, according to ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/get-insured/buy-a-policy",
        children: "FEMA"
      }), ". While flood insurance can offer homeowners additional coverage, a flood insurance policy does not cover vehicles, swimming pools, decks and patios, septic systems, and sewer backups not directly caused by flooding."]
    }), "\n", _jsx(_components.h3, {
      children: "Will Proposed FEMA Cuts Affect Flood Insurance?"
    }), "\n", _jsxs(_components.p, {
      children: ["Should the NFIP fail to receive reauthorization after its current expiration date on ", _jsx(_components.a, {
        href: "https://www.fema.gov/flood-insurance/rules-legislation/congressional-reauthorization",
        children: "September 30"
      }), ", 2025 homeowners covered by this federal program can expect significant changes. While flood insurance policies purchased and serviced through private insurance carriers will not be affected, homeowners with existing policies underwritten by the NFIP will remain covered until their policies expire, with claims paid as remaining FEMA funds allow, according to the ", _jsx(_components.a, {
        href: "https://www.nar.realtor/flood-insurance/faq-national-flood-insurance-program-expiration",
        children: "National Association of"
      }), " ", _jsx(_components.a, {
        href: "http://realtors.in",
        children: "Realtors"
      }), ". In addition, any corresponding federal requirements to purchase flood insurance would be suspended until further notice, and no new policies would be underwritten by the NFIP, a change that could affect millions of homeowners, per ", _jsx(_components.a, {
        href: "https://www.fema.gov/flood-insurance/rules-legislation/congressional-reauthorization",
        children: "FEMA"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["In place of federal financial disaster response from FEMA, President Trump identifies the White House and the Department of Homeland Security as sources of emergency response funds, according to ", _jsx(_components.a, {
        href: "https://www.insurancebusinessmag.com/us/news/catastrophe/trump-suggests-phasing-out-femas-role-in-disaster-response-538858.aspx",
        children: "Insurance Business"
      }), ". The President also proposes that state governors should take a central role in delivering financial assistance to disaster victims."]
    }), "\n", _jsx(_components.p, {
      children: "However, these changes are unlikely to occur without some setbacks. “If they dismantle FEMA after this hurricane season and don’t take the NFIP program and put it under the control of another department, it’s unclear what will happen,” says Patestos, equating it to “uncharted territory.” When asked what the future may hold for homeowners covered by policies underwritten by the NFIP, Patestos says, “Anyone that tells you they can accurately predict what might happen is speculating. The more likely outcome is they take the program and reform it in one way or the other… if I had to bet I would say that doesn’t mean lower premiums with better coverage.”"
    }), "\n", _jsxs(_components.h3, {
      children: ["Flood Damage Trends", _jsx(_components.img, {
        src: "/images/blog/AdobeStock_442416575-1024x576.webp",
        alt: "Residential neighborhood affected by flooding showing flood damage trends"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Prior to a hurricane making landfall, significant precipitation—known as Predecessor Rainfall Events (PREs)—can cause ", _jsx(_components.a, {
        href: "https://www.nssl.noaa.gov/education/svrwx101/floods/types/",
        children: "rivers to swell"
      }), " (riverine and inland flooding) and higher-than-average tides to erode the coastline (coastal flooding), according to ", _jsx(_components.a, {
        href: "https://www.climate.gov/news-features/event-tracker/hurricane-helenes-extreme-rainfall-and-catastrophic-inland-flooding",
        children: "NOAA"
      }), ". A few days before Hurricane Helene officially made landfall in September 2024, the region saw 10 to 15 inches of rainfall, with the highest totals centered over the North Carolina Mountains. In western North Carolina, a consecutive four-day period of rain prior to Hurricane Helene’s arrival resulted in historical flooding similar to that of the Great Flood of 1916."]
    }), "\n", _jsxs(_components.p, {
      children: ["Between 1980 and 2024, the NFIP paid more than $87 billion across nearly 2 million flood insurance claims, with claims made in every state, according to ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/historical-nfip-claims-information-and-trends?map=countries/us/custom/us-all-territories&region=us&miny=all&maxy=all&county=&gtype=country",
        children: "FEMA"
      }), ". The total number of flood claims submitted per year peaked in 2005, with 203,076 claims corresponding to more than $17.7 billion paid to homeowners. In 2024, the NFIP received 81,133 claims and paid out nearly $8 billion, surpassing the average claim count and payout prior to 2005 and in many years since."]
    }), "\n", _jsx(_components.p, {
      children: "The frequency of flood claims is highest in coastal regions, specifically in Florida, Texas, and Louisiana. Below is the number of claims they filed in 2024."
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Florida:"
        }), " 64,359 claims costing more than $7.07 billion"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Texas:"
        }), " 3,093 claims costing nearly $156.5 million"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Louisiana:"
        }), " 2,258 claims costing over 108.8 million"]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["FEMA currently uses multiple data points to map flood risk, but only with a 50% confidence. The ", _jsx(_components.a, {
        href: "https://www.nrdc.org/bio/rob-moore/accurate-flood-maps-are-essential-climate-adaptation",
        children: "Natural Resources Defense Council"
      }), " (NRDC) believes this low level of confidence reflects the under-representation of true flood risk and recommends that FEMA adjust its approach. Improving the certainty flood risk maps and banning ", _jsx(_components.a, {
        href: "https://www.scientificamerican.com/article/dangerous-fill-and-build-floodplain-policy-should-be-scrapped-experts-say/",
        children: "fill-and-build construction methods"
      }), " (the process of using fill dirt to elevate new houses in high-risk flood zones) can help provide homeowners with a more accurate view of their flood vulnerability and inform future home-building practices to reduce the risk and severity of loss after a flood."]
    }), "\n", _jsx(_components.h3, {
      children: "What is the Cost of Flood Insurance?"
    }), "\n", _jsxs(_components.p, {
      children: ["The national annual median cost for flood insurance is $786, according to ", _jsx(_components.a, {
        href: "https://www.fema.gov/flood-insurance/work-with-nfip/risk-rating/single-family-home",
        children: "FEMA’s"
      }), " pricing approach as of August 31, 2023. Numerous factors influence flood insurance premium costs, including the following, according to ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/get-insured/buy-a-policy",
        children: "FEMA"
      }), ":"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Home construction method"
      }), "\n", _jsx(_components.li, {
        children: "Geographical location"
      }), "\n", _jsx(_components.li, {
        children: "Home replacement cost"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Some flood insurance providers may offer discounts for homeowners who proactively reduce their risk of flood damage. For example, homeowners can elevate their water heater or electrical panels to reduce the risk of damage after a flood. Obtaining an elevation certificate—used to determine and document a home’s elevation regarding the local floodplain—can also help reduce a homeowner’s flood insurance premium."
    }), "\n", _jsx(_components.p, {
      children: "The following table shows the top three states where flood insurance is the most and least expensive. Utah is the only landlocked state listed, while both non-contiguous states (Alaska and Hawaii) also appear below:"
    }), "\n", _jsx("table", {
      className: "has-border-color has-black-border-color has-fixed-layout",
      children: _jsxs("tbody", {
        children: [_jsx("tr", {
          children: _jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            colSpan: "4",
            children: _jsx("strong", {
              children: "Annual Cost of Flood Insurance"
            })
          })
        }), _jsxs("tr", {
          children: [_jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            colSpan: "2",
            children: _jsx("strong", {
              children: "Most Expensive States"
            })
          }), _jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            colSpan: "2",
            children: _jsx("strong", {
              children: "Least Expensive States"
            })
          })]
        }), _jsxs("tr", {
          children: [_jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            children: _jsx(_components.p, {
              children: "Connecticut"
            })
          }), _jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            children: _jsx(_components.p, {
              children: "$1,173.50"
            })
          }), _jsx("td", {
            children: "Alaska"
          }), _jsx("td", {
            children: "$421.00"
          })]
        }), _jsxs("tr", {
          children: [_jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            children: _jsx(_components.p, {
              children: "Massachusetts"
            })
          }), _jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            children: _jsx(_components.p, {
              children: "$1,106.00"
            })
          }), _jsx("td", {
            children: "Utah"
          }), _jsx("td", {
            children: "$602.00"
          })]
        }), _jsxs("tr", {
          children: [_jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            children: _jsx(_components.p, {
              children: "Hawaii"
            })
          }), _jsx("td", {
            className: "has-text-align-center",
            "data-align": "center",
            children: _jsx(_components.p, {
              children: "$1,023.00"
            })
          }), _jsx("td", {
            children: "Maryland"
          }), _jsx("td", {
            children: "$629.00"
          })]
        })]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["*", _jsx(_components.em, {
        children: "These numbers represent the median current cost of insurance for single family homes under NFIP’s pricing approach."
      })]
    }), "\n", _jsx(_components.p, {
      children: "Annual flood insurance policies in the top three states with the highest number of single-family homes covered by NFIP policies in 2023 were within $10 of each other and the national median. In fact, the annual cost of flood insurance in Louisiana—where 397,824 homeowners are covered by flood insurance policies underwritten by the NFIP—mirrors the national median. In Florida ($776) and Texas ($779), the NFIP covers 894,619 and 586,680 homeowners, respectively."
    }), "\n", _jsx(_components.h3, {
      children: "How to Determine if You Need Flood Insurance?"
    }), "\n", _jsxs(_components.p, {
      children: ["Homeowners wondering “Is my house in a flood zone?” can consult the ", _jsx(_components.a, {
        href: "https://msc.fema.gov/portal/search",
        children: "FEMA Flood Map Service Center"
      }), " to find their address and determine their flood risk. Homes located in Zone V, or high-risk coastal zones, have a 26% chance of experiencing a flood over the standard 30-year mortgage period, according to ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/flood-zones-and-maps/what-is-my-flood-zone",
        children: "FEMA"
      }), ". Additional flood zones include the following:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Zone A:"
        }), " High flood risk"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Zone B & X:"
        }), " Moderate flood risk"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Zone C & X:"
        }), " Low flood risk"]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Zone D:"
        }), " Undetermined flood risk (not necessarily no flood risk)"]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["If you purchased a property located in a high-risk flood zone with a government-backed mortgage loan, your lender likely required you to obtain flood insurance. Unfortunately, most FEMA flood zone maps are outdated and inaccurate, with 40% of all flood claims occurring in low- to moderate-risk zones, per ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/flood-zones-and-maps/what-is-my-flood-risk",
        children: "FEMA"
      }), ". Since federal law doesn’t require sellers to disclose previous flooding events, and state laws vary, homeowners can use the following resources to find the corresponding answer, per ", _jsx(_components.a, {
        href: "https://www.realtor.com/advice/home-improvement/is-my-property-in-a-flood-zone/",
        children: "Realtor.com"
      }), ":"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "County hazard mitigation plan"
      }), "\n", _jsx(_components.li, {
        children: "Property title search"
      }), "\n", _jsx(_components.li, {
        children: "Tax records"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Regarding flood insurance, Patestos advises, “If you are able to afford, get it. It doesn’t need to be a large flood scenario for the insurance to be of value. It can cover situations where there’s poor drainage outside and surface water from rain gets pushed into your home.” Also consider your home’s proximity to nearby bodies of water—potential flood sources—the frequency of flooding in your surrounding area, your home’s elevation relative to the floodplain, and how your home might be constructed to reduce the damage due to a flood."
    }), "\n", _jsx(_components.h3, {
      children: "What is the Risk of Not Getting Flood Insurance?"
    }), "\n", _jsxs(_components.p, {
      children: ["Homeowners who don’t obtain flood insurance risk paying out-of-pocket for significant repairs. A single inch of floodwater can cause roughly $25,000 in damage to the home, according to ", _jsx(_components.a, {
        href: "https://agents.floodsmart.gov/sites/default/files/fema-nfip-just-one-inch-of-floodwater-flyer-06-2023.pdf",
        children: "FEMA"
      }), ". The ", _jsx(_components.a, {
        href: "https://content.naic.org/article/do-i-need-flood-insurance-why-you-should-consider-flood-policy-protect-your-home",
        children: "National Association of Insurance Commissioners"
      }), " (NAIC) also reports that 25% of floods happen outside of high-risk areas."]
    }), "\n", _jsx("iframe", {
      loading: "lazy",
      src: "https://e.infogram.com/c8157dbd-ff48-4ec8-bb69-9ca98e5aabbf?src=embed&embed_type=responsive_iframe",
      title: "flood insurance quote 2",
      allowFullScreen: "",
      allow: "fullscreen"
    }), "\n", _jsx(_components.h3, {
      children: "How to Get Flood Insurance"
    }), "\n", _jsxs(_components.p, {
      children: ["Homeowners can use the ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/policy-quote/",
        children: "NFIP Quoting Tool"
      }), " to obtain a flood insurance quote and connect with an insurance agency after answering a few standard questions. If you prefer to work with a local agent, you can use your quote to initiate the conversation. You can also call the NFIP directly at ", formatPhoneNumber(RINGBA_DNI_PHONE), " or visit their website to ", _jsx(_components.a, {
        href: "https://www.floodsmart.gov/get-insured/flood-insurance-provider",
        children: "find an insurance provider"
      }), "."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
